import { EventEmitter, type IEventEmitter } from '../../system/classes/event-emitter';
import { A9Component } from '../../system/angular';
import template from './index.html';
import './styles.less';

import { PerfectScrollBarExternalApiClass } from '../../visual/atoms/perfect-scrollbar';
import { VerticalTabItem } from '../tabConstructor';
import { type IVerticalTabMenuComponent } from '../vertical-tab-menu';

export interface ITitleTemplateItem {
    scope: angular.IScope;
    template: string;
}

export interface IVerticalTabLayoutComponent extends IEventEmitter {
    externalSelectTab(path: string[]): boolean;

    externalSelectSubTabById(id: string): boolean;

    selectDefaultTab(): void;

    hasPath(path: string[]): boolean;

    isSelected(): boolean;

    activePath(): string[];

    isCollapsed(): boolean;
}

/**
 @api.emits
  select VerticalTabItem[] = [tabObject[, subTabObject[, subSubTabObject[, subSubSubTabObject[, subSubSubSubTabObject[, subSubSubSubSubTabObject]]]]]]
 */

export type TState = {
    menu?: any;
    collapsed: boolean;
};

export interface IVerticalTabLayoutScope extends angular.IScope {
    tabs: VerticalTabItem[];
    titleHeader: ITitleTemplateItem;
    titleFooter: ITitleTemplateItem;
    state: TState;
    onReady: any;
    parentScope: angular.IScope;
    activeContent: string | undefined;
    scrollbarActive: boolean;

    dlgToggleLayout(): void;

    connectScrollbarApi(api: PerfectScrollBarExternalApiClass): void;

    connectMenuApi(api: IVerticalTabMenuComponent): void;

    contentPadding: boolean;
}

@A9Component({
    selector: 'vertical-tab-layout',
    replace: true,
    scope: {
        tabs: '<',
        titleHeader: '<?',
        titleFooter: '<?',
        state: '=',
        onReady: '&'
    },
    template() {
        return template;
    }
})
class VerticalTabLayoutComponent extends EventEmitter implements IVerticalTabLayoutComponent {
    private menuApi: IVerticalTabMenuComponent;

    protected activeElement: VerticalTabItem;
    protected scrollbarApi?: PerfectScrollBarExternalApiClass;

    constructor(
        protected $scope: IVerticalTabLayoutScope,
        protected $injector: angular.auto.IInjectorService,
        protected $rootScope: angular.IScope,
        protected $element: JQuery
    ) {
        super();
        this.initScope();
        this.initState();
        this.run();
        $scope.$on('$destroy', () => {
            this.erase();
        });
    }

    public externalSelectTab(path: string[]): boolean {
        return (this.menuApi && this.menuApi.externalSelectTab(path)) || false;
    }

    public externalSelectSubTabById(id: string): boolean {
        return (this.menuApi && this.menuApi.externalSelectSubTabById(id)) || false;
    }

    public selectDefaultTab() {
        if (this.menuApi) {
            this.menuApi.selectDefaultTab();
        }
    }

    public hasPath(path: string[]): boolean {
        return (this.menuApi && this.menuApi.hasPath(path)) || false;
    }

    public activePath(): string[] {
        return (this.menuApi && this.menuApi.activePath()) || [];
    }

    public isSelected(): boolean {
        return (this.menuApi && this.menuApi.isSelectedSomething()) || false;
    }

    public isCollapsed(): boolean {
        return !!this.$scope.state.collapsed;
    }

    private initScope(): void {
        const $scope = this.$scope;
        $scope.parentScope = $scope.$parent;
        $scope.activeContent = '';
        $scope.scrollbarActive = false;

        this.$scope.connectMenuApi = api => {
            this.menuApi = api;
            $scope.onReady({ api: this });
            api.on('select', data => {
                this.setActiveElement((data && data.length && data[data.length - 1]) || null);
                this.emit('select', data);
            });
        };

        // todo catch detach scrollbar
        $scope.connectScrollbarApi = (api: PerfectScrollBarExternalApiClass) => {
            api.enablePointerEventsOptimization();
            this.scrollbarApi = api;
            this.activeElement.setScrollbarApi(api);
        };

        $scope.dlgToggleLayout = () => {
            const willBeCollapsed = !$scope.state.collapsed;
            if (willBeCollapsed) {
                //delete this.menuScrollbarApi;
            }
            $scope.state.collapsed = willBeCollapsed;
            this.setupCollapsedStateClass();
            window.setTimeout(() => {
                this.$scope.$parent.$broadcast('awl-viewport-resize');
            }, 100);
        };
    }

    private setActiveElement(tab: VerticalTabItem): void {
        const $scope = this.$scope;
        $scope.activeContent = (tab && tab.content) || '';
        const oldScrollbar = $scope.scrollbarActive;
        const newScrollbar = (tab && !!tab.scrollbar) || false;
        $scope.contentPadding = (tab && !!tab.padding) || false;

        if (this.activeElement /* && this.activeElement.deactivate*/) {
            this.activeElement.deactivate();
        }
        this.activeElement = tab;

        if (oldScrollbar != newScrollbar) {
            if (!oldScrollbar && !newScrollbar) {
                // ???
            } else if (oldScrollbar && !newScrollbar) {
                // perfect scrollbar scrope will be destroyed because of template ng-if condition change
                delete this.scrollbarApi;
            } else if (!oldScrollbar && newScrollbar) {
                // ???
            } else if (oldScrollbar && newScrollbar) {
                if (this.activeElement) {
                    const api = this.scrollbarApi;
                    if (!api) return;
                    this.activeElement.setScrollbarApi(api);
                    api.setOffsetY(0);
                }
            }
        }

        $scope.scrollbarActive = newScrollbar;
    }

    private initState(): void {
        const $scope = this.$scope;
        $scope.state = $scope.state || {
            collapsed: false
        };
        this.setupCollapsedStateClass();
    }

    protected setupCollapsedStateClass(): void {
        // angular is too $low for this
        // !! requestAnimationFrame causes delayed update and double reflow
        //		window.requestAnimationFrame(()=>{
        this.$element[0].classList[this.$scope.state.collapsed ? 'add' : 'remove']('state-collapsed');
        //		});
    }

    protected run(): void {
        const $scope = this.$scope;
        $scope.$watch('tabs', () => {
            if ($scope.tabs) {
                this.initState();
            }
        });
    }
}
